import { useState, useEffect } from 'react';
import { Label, Switch } from '@playcastdotio/Shadcn';
import { PlaycastReactFunctions } from '../PlaycastWrapper';

export const Notifications = ({ context }: { context: PlaycastReactFunctions }) => {
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [smsNotifications, setSmsNotifications] = useState(true);

  // Initialize from localStorage preferences and keep in sync
  useEffect(() => {
    const preferences = context.stateStores.preferenceState;
    setEmailNotifications(preferences.email.hostMode);
    setSmsNotifications(preferences.phone.hostMode);
  }, [context.stateStores.preferenceState]);

  // Update local state when preference state changes
  useEffect(() => {
    const preferences = context.stateStores.preferenceState;
    setEmailNotifications(preferences.email.hostMode);
    setSmsNotifications(preferences.phone.hostMode);
  }, [context.stateStores.preferenceState.email.hostMode, context.stateStores.preferenceState.phone.hostMode]);

  const handleEmailToggle = async (checked: boolean) => {
    setEmailNotifications(checked);

    // Get the original localStorage values as a fallback
    const originalLocalStorage = (() => {
      try {
        const stored = localStorage.getItem('preferenceState');
        return stored ? JSON.parse(stored) : null;
      } catch (e) {
        return null;
      }
    })();
    
    // Update only the specific preference being changed
    const currentPreferences = context.stateStores.preferenceState;
    
    // Use original localStorage values as fallback to avoid corrupted state
    const safePhonePreferences = originalLocalStorage?.phone || {
      applicationUpdates: true,
      playcastEvents: true,
      feedbackSurveys: true,
      newsletter: true,
      hostMode: true,
    };
    
    // Only update the hostMode property for email, keep phone unchanged
    const updatedEmailPreferences = {
      applicationUpdates: currentPreferences.email.applicationUpdates,
      playcastEvents: currentPreferences.email.playcastEvents,
      feedbackSurveys: currentPreferences.email.feedbackSurveys,
      newsletter: currentPreferences.email.newsletter,
      hostMode: checked,
    };
    
    // Save to localStorage using the existing save function
    if (context.electronFunctions) {
      context.electronFunctions.savePreferences(safePhonePreferences, updatedEmailPreferences);
    } else if (context.remoteControlFunctions) {
      context.remoteControlFunctions.savePreferences(safePhonePreferences, updatedEmailPreferences);
    } else {
      console.log('[notifications] No savePreferences function available');
    }
  };

  const handleSmsToggle = async (checked: boolean) => {
    setSmsNotifications(checked);

    // Get the original localStorage values as a fallback
    const originalLocalStorage = (() => {
      try {
        const stored = localStorage.getItem('preferenceState');
        return stored ? JSON.parse(stored) : null;
      } catch (e) {
        return null;
      }
    })();
    
    // Update only the specific preference being changed
    const currentPreferences = context.stateStores.preferenceState;
    
    // Use original localStorage values as fallback to avoid corrupted state
    const safeEmailPreferences = originalLocalStorage?.email || {
      applicationUpdates: true,
      playcastEvents: true,
      feedbackSurveys: true,
      newsletter: true,
      hostMode: true,
    };
    
    // Only update the hostMode property for phone, keep email unchanged
    const updatedPhonePreferences = {
      applicationUpdates: currentPreferences.phone.applicationUpdates,
      playcastEvents: currentPreferences.phone.playcastEvents,
      feedbackSurveys: currentPreferences.phone.feedbackSurveys,
      newsletter: currentPreferences.phone.newsletter,
      hostMode: checked,
    };
    
    // Save to localStorage using the existing save function
    if (context.electronFunctions) {
      context.electronFunctions.savePreferences(updatedPhonePreferences, safeEmailPreferences);
    } else if (context.remoteControlFunctions) {
      context.remoteControlFunctions.savePreferences(updatedPhonePreferences, safeEmailPreferences);
    } else {
      console.log('[notifications] No savePreferences function available');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <Label htmlFor="email-notifications">Email Notifications</Label>
          <p className="text-sm text-muted-foreground">Receive notifications via email</p>
        </div>
        <Switch
          id="email-notifications"
          checked={emailNotifications}
          onCheckedChange={handleEmailToggle}
        />
      </div>

      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <Label htmlFor="sms-notifications">SMS Notifications</Label>
          <p className="text-sm text-muted-foreground">Receive notifications via SMS</p>
        </div>
        <Switch
          id="sms-notifications"
          checked={smsNotifications}
          onCheckedChange={handleSmsToggle}
        />
      </div>
    </div>
  );
}; 